import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/client";
import Logo from "../../components/Logo";
import { hashPassword } from "../../api/crypto";

const PAISES = [
  "Venezuela", "Colombia", "México", "Argentina", "Chile", "Perú",
  "Ecuador", "Bolivia", "Uruguay", "Paraguay", "Panamá", "Costa Rica",
  "Guatemala", "Honduras", "El Salvador", "Nicaragua", "República Dominicana",
  "Cuba", "Puerto Rico", "España", "Estados Unidos", "Otro",
];

const slugify = (text) =>
  text.toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

function passwordStrength(pwd) {
  if (!pwd) return null;
  let score = 0;
  if (pwd.length >= 8)  score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  if (score <= 1) return { label: "Muy débil", color: "#ef4444", pct: 20 };
  if (score === 2) return { label: "Débil",     color: "#f97316", pct: 40 };
  if (score === 3) return { label: "Media",     color: "#eab308", pct: 60 };
  if (score === 4) return { label: "Fuerte",    color: "#22c55e", pct: 80 };
  return                 { label: "Muy fuerte", color: "#16a34a", pct: 100 };
}

function FieldError({ msg }) {
  if (!msg) return null;
  return <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><span>✕</span>{msg}</p>;
}

function InputWrap({ label, error, touched, children, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className={`rounded-lg border transition-colors ${touched && error ? "border-red-400 ring-1 ring-red-300" : "border-gray-300"}`}>
        {children}
      </div>
      {touched && error ? <FieldError msg={error} /> : hint ? <p className="text-xs text-gray-400 mt-1">{hint}</p> : null}
    </div>
  );
}

export default function Register() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    nombre_completo: "", nombre_tienda: "", pais: "Venezuela",
    email: "", password: "", confirmar: "",
  });
  const [touched, setTouched] = useState({});
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [loading, setLoading] = useState(false);

  const slug = slugify(form.nombre_tienda);

  // ── Validaciones por campo ───────────────────────────────────────────────
  const errores = {
    nombre_completo: (() => {
      const v = form.nombre_completo.trim();
      if (!v) return "El nombre es obligatorio";
      if (v.length < 3) return "Debe tener al menos 3 caracteres";
      if (v.length > 100) return "Máximo 100 caracteres";
      return "";
    })(),
    nombre_tienda: (() => {
      const v = form.nombre_tienda.trim();
      if (!v) return "El nombre de la tienda es obligatorio";
      if (v.length < 3) return "Debe tener al menos 3 caracteres";
      if (slug.length < 3) return "El nombre genera una URL muy corta, agrega más letras";
      if (v.length > 100) return "Máximo 100 caracteres";
      return "";
    })(),
    email: (() => {
      const v = form.email.trim();
      if (!v) return "El correo es obligatorio";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return "Ingresa un correo válido";
      return "";
    })(),
    password: (() => {
      if (!form.password) return "La contraseña es obligatoria";
      if (form.password.length < 8) return "Mínimo 8 caracteres";
      return "";
    })(),
    confirmar: (() => {
      if (!form.confirmar) return "Confirma tu contraseña";
      if (form.confirmar !== form.password) return "Las contraseñas no coinciden";
      return "";
    })(),
  };

  const hayErrores = Object.values(errores).some(Boolean);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleBlur = (e) => {
    setTouched((p) => ({ ...p, [e.target.name]: true }));
  };

  const inputClass = "w-full px-3 py-2.5 text-sm focus:outline-none bg-transparent rounded-lg";

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Marcar todos como tocados para mostrar todos los errores
    setTouched({ nombre_completo: true, nombre_tienda: true, email: true, password: true, confirmar: true });
    if (hayErrores) return;

    setSubmitError("");
    setLoading(true);
    try {
      const hashedPwd = await hashPassword(form.password);
      const { data } = await api.post("/auth/registro", {
        nombre_completo: form.nombre_completo.trim(),
        nombre_tienda: form.nombre_tienda.trim(),
        pais: form.pais,
        email: form.email.trim().toLowerCase(),
        password: hashedPwd,
      });
      login(data.access_token);
      navigate("/dashboard");
    } catch (err) {
      setSubmitError(err.response?.data?.detail || "Error al registrarse. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const strength = passwordStrength(form.password);

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg, #0A1128 0%, #1a1f4e 60%, #0A1128 100%)" }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8">

        <div className="flex flex-col items-center mb-6">
          <Logo width={200} />
          <h1 className="text-lg font-bold text-gray-900 mt-3">Crea tu catálogo digital</h1>
          <p className="text-gray-500 text-sm mt-1">Gratis. Sin tarjeta de crédito.</p>
        </div>

        {submitError && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm mb-4">
            {submitError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="space-y-4">

          {/* Nombre completo */}
          <InputWrap label="Nombre completo" error={errores.nombre_completo} touched={touched.nombre_completo}>
            <input
              type="text" name="nombre_completo" autoComplete="name"
              value={form.nombre_completo} onChange={handleChange} onBlur={handleBlur}
              placeholder="Juan Pérez"
              className={inputClass}
            />
          </InputWrap>

          {/* Nombre de la tienda */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de tu tienda</label>
            <div className={`rounded-lg border transition-colors ${touched.nombre_tienda && errores.nombre_tienda ? "border-red-400 ring-1 ring-red-300" : "border-gray-300"}`}>
              <input
                type="text" name="nombre_tienda"
                value={form.nombre_tienda} onChange={handleChange} onBlur={handleBlur}
                placeholder="Mi Tienda Genial"
                className={inputClass}
              />
            </div>
            {touched.nombre_tienda && errores.nombre_tienda ? (
              <FieldError msg={errores.nombre_tienda} />
            ) : slug ? (
              /* Preview URL corregida */
              <div className="mt-2 flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
                <svg className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <span className="text-xs text-indigo-600">
                  Tu catálogo estará en:{" "}
                  <strong className="font-bold">tucatalogo.net/catalogo/{slug}</strong>
                </span>
              </div>
            ) : null}
          </div>

          {/* País */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">País</label>
            <div className="rounded-lg border border-gray-300">
              <select
                name="pais" value={form.pais} onChange={handleChange}
                className={inputClass}
              >
                {PAISES.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {/* Email */}
          <InputWrap label="Correo electrónico" error={errores.email} touched={touched.email}>
            <input
              type="email" name="email" autoComplete="email"
              value={form.email} onChange={handleChange} onBlur={handleBlur}
              placeholder="tu@correo.com"
              className={inputClass}
            />
          </InputWrap>

          {/* Contraseña */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
            <div className={`rounded-lg border transition-colors flex items-center ${touched.password && errores.password ? "border-red-400 ring-1 ring-red-300" : "border-gray-300"}`}>
              <input
                type={showPwd ? "text" : "password"} name="password" autoComplete="new-password"
                value={form.password} onChange={handleChange} onBlur={handleBlur}
                placeholder="Mínimo 8 caracteres"
                className="flex-1 px-3 py-2.5 text-sm focus:outline-none bg-transparent rounded-l-lg"
              />
              <button type="button" onClick={() => setShowPwd((v) => !v)}
                className="px-3 text-gray-400 hover:text-gray-600 text-xs font-medium">
                {showPwd ? "Ocultar" : "Ver"}
              </button>
            </div>
            {/* Indicador de fuerza */}
            {form.password && strength && (
              <div className="mt-1.5">
                <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-300"
                    style={{ width: `${strength.pct}%`, background: strength.color }} />
                </div>
                <p className="text-xs mt-0.5 font-medium" style={{ color: strength.color }}>{strength.label}</p>
              </div>
            )}
            {touched.password && errores.password && <FieldError msg={errores.password} />}
          </div>

          {/* Confirmar contraseña */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar contraseña</label>
            <div className={`rounded-lg border transition-colors flex items-center ${touched.confirmar && errores.confirmar ? "border-red-400 ring-1 ring-red-300" : !errores.confirmar && touched.confirmar ? "border-green-400" : "border-gray-300"}`}>
              <input
                type={showConfirm ? "text" : "password"} name="confirmar" autoComplete="new-password"
                value={form.confirmar} onChange={handleChange} onBlur={handleBlur}
                placeholder="Repite tu contraseña"
                className="flex-1 px-3 py-2.5 text-sm focus:outline-none bg-transparent rounded-l-lg"
              />
              <button type="button" onClick={() => setShowConfirm((v) => !v)}
                className="px-3 text-gray-400 hover:text-gray-600 text-xs font-medium">
                {showConfirm ? "Ocultar" : "Ver"}
              </button>
            </div>
            {touched.confirmar && errores.confirmar && <FieldError msg={errores.confirmar} />}
            {touched.confirmar && !errores.confirmar && form.confirmar && (
              <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><span>✓</span>Las contraseñas coinciden</p>
            )}
          </div>

          <button
            type="submit" disabled={loading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm mt-2"
          >
            {loading ? "Creando tu catálogo..." : "Crear mi catálogo gratis →"}
          </button>

          <p className="text-xs text-gray-400 text-center">
            Al registrarte aceptas nuestros términos de servicio y política de privacidad.
          </p>
        </form>

        <p className="text-center text-sm text-gray-500 mt-4">
          ¿Ya tienes cuenta?{" "}
          <Link to="/login" className="text-indigo-600 font-medium hover:underline">Inicia sesión</Link>
        </p>
      </div>
    </div>
  );
}
